import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { serverMessage } from "@/lib/apiError";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AddressPicker,
  addressSelectionBlocker,
  type AddressSelection,
} from "@/components/shared/AddressPicker";
import { createUserAddress } from "@/services/userAddressesService";

/**
 * The consumer's address book.
 *
 * WHY IT EXISTS. Since spec 8c2aab49 a consumer picks a delivery address
 * during RFQ intake, and anything they type there is saved to their book —
 * with no screen anywhere to review, correct or remove it. A typo in a street
 * number was unfixable except by creating a SECOND address on the next
 * request, which then sat beside the wrong one forever.
 *
 * BUILT ON AddressPicker, which already does the saved list, Places entry,
 * editing and deletion. But it does NOT save a NEW address — in the wizard the
 * parent's Continue button does that, so on a standalone page the form simply
 * had no way out. That is what the save action below is: the parent action the
 * picker has always assumed and this page was missing.
 */
export function UserAddressesPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<AddressSelection>(null);

  const createMutation = useMutation({
    mutationFn: createUserAddress,
    onSuccess: async (created) => {
      toast.success(t("addresses.saveSuccess"));
      // Re-point at the saved row rather than clearing: the person has just
      // described an address and should see it listed, not an empty form that
      // looks like the save was lost.
      setSelection({
        kind: "saved",
        addressId: created.id,
        contactPhone: created.contactPhone ?? null,
        contactPersonName: created.contactPersonName ?? null,
        courierNotes: created.courierNotes ?? null,
      });
      await queryClient.invalidateQueries({ queryKey: ["me", "addresses"] });
    },
    onError: (err: unknown) =>
      toast.error(serverMessage(err) ?? t("addresses.saveError")),
  });

  // Only a NEW address can be saved from here. A saved selection is already
  // stored, and the picker's own edit/delete handle changing it.
  const isNew = selection?.kind === "new";
  const blocker = isNew ? addressSelectionBlocker(selection) : null;

  return (
    <div className="space-y-6 p-4 md:p-8">
      <PageHeader title={t("addresses.title")} description={t("addresses.description")} />

      <Card className="shadow-sm">
        <CardContent className="space-y-4 pt-6">
          <AddressPicker
            selection={selection}
            onSelectionChange={setSelection}
            disabled={createMutation.isPending}
            showContactFields
            showCourierNotesField
            idPrefix="address-book"
          />

          {isNew ? (
            /* Action row: the button sits right, the reason for a disabled
               button sits left via `mr-auto` rather than trailing it. A hint
               to the RIGHT of the control it explains reads as a caption of
               the next thing, not of the button. */
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border/60 pt-4">
              {/* The REASON, not just a disabled button. The picker's own
                  helper already knows whether the gap is the address or the
                  phone, so the hint and the button can never disagree. */}
              {blocker ? (
                <p className="mr-auto text-xs text-muted-foreground">
                  {t(
                    blocker === "contactPhone"
                      ? "addresses.needsPhone"
                      : "addresses.needsAddress",
                  )}
                </p>
              ) : null}
              <Button
                disabled={blocker != null || createMutation.isPending}
                onClick={() => {
                  if (selection?.kind !== "new") return;
                  createMutation.mutate(selection.payload);
                }}
              >
                {createMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                ) : null}
                {t("addresses.save")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

export default UserAddressesPage;
